import { Component, Input, OnInit } from '@angular/core';
import {
  ColDef,
  GetRowIdParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  NewValueParams,
  RowModelType,
  ValueFormatterParams,
  ValueParserParams,
} from 'ag-grid-community';
import 'ag-grid-enterprise';
import { FlagsCellRendererComponent } from '../flags-cell-renderer/flags-cell-renderer.component';
import { ModalCellRendererComponent } from '../modal-cell-renderer/modal-cell-renderer.component';
import { ModalConfig } from '../models/modal-system.interfaces';
import {
  CarrierOrdersGridService,
  DynamicContactAccessContactProfile,
  DynamicGridConfigItem,
  IdentityScope,
} from '../services/carrier-orders-grid/carrier-orders-grid.service';
import { GridModalDispatcherService } from '../services/carrier-orders-grid/grid-modal-dispatcher.service';
import { AuthService, CurrentUser } from '../auth.service';

interface CarrierWorkOrdersGridState {
  columnState?: ReturnType<GridApi['getColumnState']>;
  filterModel?: Record<string, any> | null;
  paginationPageSize?: number;
}

interface CarrierWorkOrdersInsight {
  key: string;
  title: string;
  value: number;
  description: string;
  tone: 'sky' | 'amber' | 'emerald' | 'rose';
  icon: string;
}

@Component({
  selector: 'app-carrier-work-orders',
  templateUrl: './carrier-work-orders.component.html',
  styleUrls: ['./carrier-work-orders.component.scss'],
})
export class CarrierWorkOrdersComponent implements OnInit {
  @Input() identityScope: IdentityScope = 'carrier';
  @Input() pageTitle = 'Carrier Work Orders';
  @Input() scopeLabel = 'Carrier Scope';
  @Input() scopeNoun = 'carrier';
  @Input() scopeProfileType = 1;
  @Input() detailsRoute = '/carrier_order_details';

  gridApi!: GridApi<any>;

  themeClass = 'ag-theme-ulaap';
  themeMode: 'dark' | 'light' = 'dark';
  rowModelType: RowModelType = 'clientSide';

  columnDefs: ColDef[] = [];
  rowData: any[] = [];
  operationalInsights: CarrierWorkOrdersInsight[] = [];
  hasOperationalCoverage = false;

  loading = true;
  errorMessage = '';
  canSelectCarrierScope = false;
  carrierOptionsLoading = false;
  carrierOptionsError = '';
  selectedCarrierContactUuid = '';
  carrierScopeOptions: DynamicContactAccessContactProfile[] = [];

  private dynamicConfig: DynamicGridConfigItem[] = [];
  private mutedEdits = new Set<string>();
  private readonly blockedFlagFields = new Set<string>([
    'v_lading_site_checks.technician_required',
  ]);
  private readonly carrierScopeRoleNames = new Set(['ADMIN', 'SUPER_ADMIN', 'MASTER', 'DISPATCHER']);
  private authContextResolved = false;
  private gridStateListenersBound = false;
  private isRestoringGridState = false;
  private get gridStateStorageKey(): string {
    return `${this.identityScope}-work-orders-grid-state-v1`;
  }

  private get themeStorageKey(): string {
    return `${this.identityScope}-work-orders-theme`;
  }

  private get carrierScopeStorageKey(): string {
    return `${this.identityScope}-work-orders-selected-contact-uuid`;
  }

  gridOptions: GridOptions<any> = {
    rowHeight: 46,
    headerHeight: 44,
    animateRows: true,
    pagination: true,
    paginationPageSize: 50,
    paginationPageSizeSelector: [25, 50, 100, 250],
    stopEditingWhenCellsLoseFocus: true,
    singleClickEdit: true,
    undoRedoCellEditing: true,
    undoRedoCellEditingLimit: 50,
    tooltipShowDelay: 400,
    enableCellTextSelection: true,
    getRowId: (params: GetRowIdParams<any>) => params.data?.lading_uuid,
    sideBar: {
      hiddenByDefault: true,
      toolPanels: ['columns', 'filters'],
    },
    statusBar: {
      statusPanels: [
        { statusPanel: 'agTotalRowCountComponent', align: 'left' },
        { statusPanel: 'agFilteredRowCountComponent' },
        { statusPanel: 'agSelectedRowCountComponent' },
      ],
    },
  };

  defaultColDef: ColDef = {
    sortable: true,
    filter: 'agTextColumnFilter',
    filterParams: {
      caseSensitive: false,
      buttons: ['apply', 'clear', 'reset'],
    },
    resizable: true,
  };

  constructor(
    private cwoService: CarrierOrdersGridService,
    private modalDispatcher: GridModalDispatcherService,
    private authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.initializeTheme();
    this.initializeCarrierScopeControls();
  }

  onGridReady(e: GridReadyEvent): void {
    this.gridApi = e.api;
    this.bindGridStateListeners();
    this.maybeLoadInitialGrid();
  }

  toggleTheme(): void {
    this.themeMode = this.themeMode === 'dark' ? 'light' : 'dark';
    window.localStorage.setItem(this.themeStorageKey, this.themeMode);
  }

  getThemeIcon(): string {
    return this.themeMode === 'dark' ? 'fa-solid fa-sun' : 'fa-solid fa-moon';
  }

  getThemeLabel(): string {
    return this.themeMode === 'dark' ? 'Light' : 'Dark';
  }

  private loadGrid(): void {
    if (this.canSelectCarrierScope && !this.selectedCarrierContactUuid) {
      this.loading = false;
      this.errorMessage = '';
      this.clearGridData();
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    this.cwoService.getDynamicWorkOrdersGrid(
      'work_orders',
      this.identityScope,
      this.selectedCarrierContactUuid || undefined,
    ).subscribe({
      next: (resp) => {
        this.loading = false;

        if (!resp || resp.success !== true) {
          this.errorMessage = resp?.message || 'Failed to load work orders.';
          return;
        }

        this.dynamicConfig = (resp.config || [])
          .filter((c: any) => c && c.is_visible)
          .sort((a: any, b: any) => (a.display_order ?? 0) - (b.display_order ?? 0));

        const defs = this.buildColDefsFromConfig(this.dynamicConfig);
        const data = resp.data || [];

        this.columnDefs = defs;
        this.rowData = data;
        this.refreshOperationalInsights();
        this.applyGridData(defs, data);
      },
      error: (err) => {
        this.loading = false;
        this.errorMessage = 'Network error loading work orders.';
        console.error(`${this.pageTitle} load error:`, err);
      },
    });
  }

  refreshData(): void {
    this.loadGrid();
  }

  onCarrierSelectionChange(contactUuid: string): void {
    this.selectedCarrierContactUuid = contactUuid || '';
    this.persistSelectedCarrierScope();

    if (!this.selectedCarrierContactUuid) {
      this.errorMessage = '';
      this.loading = false;
      this.clearGridData();
      return;
    }

    this.loadGrid();
  }

  get selectedCarrierScopeLabel(): string {
    if (!this.selectedCarrierContactUuid) {
      return this.canSelectCarrierScope
        ? `Select a ${this.scopeNoun} to load work orders`
        : 'Current login scope';
    }

    return this.carrierScopeOptions.find(
      (option) => option.contact_uuid === this.selectedCarrierContactUuid,
    )?.contact_organization || `Selected ${this.scopeNoun} scope`;
  }

  resetSavedView(): void {
    window.localStorage.removeItem(this.gridStateStorageKey);

    const api: any = this.gridApi;
    api?.closeToolPanel?.();

    this.loadGrid();
  }

  private initializeTheme(): void {
    const storedTheme = window.localStorage.getItem(this.themeStorageKey);
    if (storedTheme === 'dark' || storedTheme === 'light') {
      this.themeMode = storedTheme;
      return;
    }

    const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;
    this.themeMode = prefersDark ? 'dark' : 'light';
  }

  private initializeCarrierScopeControls(): void {
    this.authService.ensureCurrentUserLoaded().subscribe({
      next: (currentUser) => {
        this.canSelectCarrierScope = this.userCanSelectCarrierScope(currentUser);
        this.authContextResolved = true;

        if (this.canSelectCarrierScope) {
          this.loadCarrierScopeOptions();
        }

        this.maybeLoadInitialGrid();
      },
      error: (error) => {
        this.authContextResolved = true;
        console.warn(`${this.scopeLabel} auth context failed to load`, error);
        this.maybeLoadInitialGrid();
      },
    });
  }

  private userCanSelectCarrierScope(currentUser: CurrentUser | null): boolean {
    if (!currentUser?.roles?.length) {
      return false;
    }

    return currentUser.roles.some((role) => this.carrierScopeRoleNames.has(role));
  }

  private persistSelectedCarrierScope(): void {
    if (!this.selectedCarrierContactUuid) {
      window.localStorage.removeItem(this.carrierScopeStorageKey);
      return;
    }

    window.localStorage.setItem(this.carrierScopeStorageKey, this.selectedCarrierContactUuid);
  }

  private restoreSelectedCarrierScope(): void {
    if (!this.canSelectCarrierScope || this.selectedCarrierContactUuid) return;

    const storedContactUuid = (window.localStorage.getItem(this.carrierScopeStorageKey) || '').toLowerCase();
    if (!storedContactUuid) return;

    const storedCarrier = this.carrierScopeOptions.find(
      (option) => String(option.contact_uuid || '').toLowerCase() === storedContactUuid,
    );

    if (storedCarrier?.contact_uuid) {
      this.selectedCarrierContactUuid = storedCarrier.contact_uuid;
      return;
    }

    window.localStorage.removeItem(this.carrierScopeStorageKey);
  }

  private loadCarrierScopeOptions(): void {
    this.carrierOptionsLoading = true;
    this.carrierOptionsError = '';

    this.cwoService.getDynamicContactAccessContactList(this.scopeProfileType).subscribe({
      next: (resp) => {
        this.carrierOptionsLoading = false;

        if (!resp || Number(resp.code) === 403) {
          this.carrierOptionsError = resp?.message || `${this.scopeLabel} list is unavailable.`;
          this.carrierScopeOptions = [];
          return;
        }

        this.carrierScopeOptions = (resp.contact_profiles || [])
          .filter((profile) => !!profile?.contact_uuid)
          .sort((left, right) =>
            String(left.contact_organization || '').localeCompare(String(right.contact_organization || ''))
          );

        this.restoreSelectedCarrierScope();
        this.maybeLoadInitialGrid();
      },
      error: (error) => {
        this.carrierOptionsLoading = false;
        this.carrierOptionsError = `Unable to load ${this.scopeNoun} access profiles.`;
        this.carrierScopeOptions = [];
        console.error(`${this.scopeLabel} options load error:`, error);
      },
    });
  }

  private maybeLoadInitialGrid(): void {
    if (!this.gridApi || !this.authContextResolved) {
      return;
    }

    if (this.canSelectCarrierScope && !this.selectedCarrierContactUuid) {
      this.loading = false;
      this.errorMessage = '';
      this.clearGridData();
      return;
    }

    this.loadGrid();
  }

  private clearGridData(): void {
    this.columnDefs = [];
    this.rowData = [];
    this.dynamicConfig = [];
    this.operationalInsights = [];
    this.hasOperationalCoverage = false;
  }

  private applyGridData(defs: ColDef[], data: any[]): void {
    if (!this.gridApi) return;

    const api: any = this.gridApi;
    if (typeof api.setGridOption === 'function') {
      api.setGridOption('columnDefs', defs);
      api.setGridOption('rowData', data);
    } else {
      this.gridApi.setGridOption?.('columnDefs', defs);
      this.gridApi.setGridOption?.('rowData', data);
    }

    window.setTimeout(() => this.restoreGridState(), 0);
  }

  private buildColDefsFromConfig(cfg: DynamicGridConfigItem[]): ColDef[] {
    const defs: ColDef[] = [];
    const checkboxCols = cfg.filter((item) => item.input_type === 'checkbox');
    let flagsInserted = false;

    defs.push({
      headerName: 'UUID',
      field: 'lading_uuid',
      hide: true,
    });

    for (const col of cfg) {
      const dbField = col.db_field;

      if (col.input_type === 'checkbox') {
        if (!flagsInserted && checkboxCols.length) {
          defs.push(this.buildFlagsColDef(checkboxCols));
          flagsInserted = true;
        }
        defs.push(this.buildHiddenCheckboxColDef(col, dbField));
        continue;
      }

      const def: ColDef = {
        headerName: col.header_name,
        colId: dbField,
        sortable: col.is_sortable !== false,
        editable: false,
        flex: 1,
        minWidth: 132,
        headerTooltip: col.header_name,
        valueGetter: (p) => (p.data ? p.data[dbField] : null),
        valueSetter: (p) => {
          if (!p.data) return false;
          p.data[dbField] = p.newValue;
          return true;
        },
        filterValueGetter: (p) => (p.data ? p.data[dbField] : null),
      };

      if (this.isStatusField(dbField)) {
        this.configureStatusCol(def);
      } else if (this.isDateTimeField(col, dbField)) {
        this.configureDateTimeCol(def, col, dbField);
      } else if (this.isDateField(col, dbField)) {
        this.configureDateCol(def, col, dbField);
      } else {
        switch (col.input_type) {
          case 'readonly_text':
            def.editable = false;
            def.cellClass = 'cell-readonly cell-readonly-text';
            break;

          case 'modal':
            this.configureModalCol(def, col, dbField);
            break;

          case 'text':
          default:
            if (col.is_editable) {
              def.editable = true;
              def.cellClass = 'cell-editable cell-text';
              def.cellEditor = 'agTextCellEditor';
            } else {
              def.cellClass = 'cell-readonly cell-readonly-text';
            }
            break;
        }
      }

      if (col.is_editable && col.input_type !== 'modal') {
        def.onCellValueChanged = (e: NewValueParams) =>
          this.onDynamicCellValueChanged(e, col, dbField);
      }

      defs.push(def);
    }

    defs.unshift({
      headerName: '',
      colId: '__details_action',
      width: 50,
      maxWidth: 50,
      minWidth: 50,
      sortable: false,
      filter: false,
      resizable: false,
      pinned: 'left',
      cellClass: 'cell-action-icon',
      cellRenderer: (params: ICellRendererParams) => {
        if (!params.data) return '';
        const btn = document.createElement('button');
        btn.className = 'cwo-details-btn';
        btn.title = 'Open details';
        btn.innerHTML = '<i class="fa-solid fa-up-right-from-square"></i>';
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          const uuid = params.data['lading_uuid'];
          if (uuid) {
            const query = new URLSearchParams({ identity_scope: this.identityScope });
            if (this.selectedCarrierContactUuid) {
              query.set('contact_uuid', this.selectedCarrierContactUuid);
            }
            window.open(`${this.detailsRoute}/${uuid}?${query.toString()}`, '_blank');
          }
        });
        return btn;
      },
    });

    return defs;
  }

  private buildHiddenCheckboxColDef(col: DynamicGridConfigItem, dbField: string): ColDef {
    return {
      headerName: col.header_name,
      colId: dbField,
      field: dbField,
      hide: true,
      editable: this.isFlagEditable(col),
      cellDataType: 'boolean',
      valueGetter: (p) => (p.data ? p.data[dbField] : null),
      valueSetter: (p) => {
        if (!p.data) return false;
        p.data[dbField] = p.newValue;
        return true;
      },
      onCellValueChanged: (e: NewValueParams) => this.onDynamicCellValueChanged(e, col, dbField),
    };
  }

  private buildFlagsColDef(checkboxCols: DynamicGridConfigItem[]): ColDef {
    const width = Math.min(Math.max(checkboxCols.length * 68 + 48, 240), 360);

    return {
      headerName: 'Flags',
      colId: '__flags',
      minWidth: width,
      width,
      maxWidth: width + 24,
      flex: 0,
      sortable: false,
      filter: false,
      resizable: true,
      editable: false,
      headerTooltip: checkboxCols.map((item) => item.header_name).join(' • '),
      cellClass: 'cell-flags',
      cellRenderer: FlagsCellRendererComponent,
      cellRendererParams: {
        flagsConfig: checkboxCols.map((item) => ({
          ...item,
          is_editable: this.isFlagEditable(item),
        })),
        onToggleFlag: (ctx: {
          rowData: Record<string, any>;
          node: any;
          col: DynamicGridConfigItem;
          dbField: string;
          newValue: boolean;
        }) => this.onBooleanFlagToggle(ctx.rowData, ctx.node, ctx.col, ctx.dbField, ctx.newValue),
      },
    };
  }

  private configureModalCol(def: ColDef, col: DynamicGridConfigItem, dbField: string): void {
    const modalConfig = (col as any).modal_config as ModalConfig | undefined;

    def.editable = false;
    def.cellClass = 'cell-modal';
    def.minWidth = modalConfig?.modal_type === 'labor_review' ? 270 : 210;
    def.flex = modalConfig?.modal_type === 'labor_review' ? 1.25 : 1.08;

    if (!modalConfig?.modal_type) {
      console.warn(`[CWO] Column "${dbField}" has input_type=modal but no modal_config.modal_type.`);
      def.cellClass = 'cell-readonly cell-readonly-text';
      return;
    }

    def.cellRenderer = ModalCellRendererComponent;
    def.cellRendererParams = {
      modalConfig,
      dbField,
      headerName: col.header_name,
      onModalClick: (ctx: {
        lading_uuid: string;
        rowData: Record<string, any>;
        modalConfig: ModalConfig;
        headerName: string;
      }) => {
        const ref = this.modalDispatcher.dispatch(
          ctx.lading_uuid,
          ctx.rowData,
          ctx.modalConfig,
          ctx.headerName,
          this.selectedCarrierContactUuid || undefined,
        );

        if (ref) {
          ref.afterClosed().subscribe((result: any) => {
            if (result?.refreshGrid) {
              this.refreshData();
            }
          });
        }
      },
    };
  }

  private onBooleanFlagToggle(
    rowData: Record<string, any>,
    node: any,
    col: DynamicGridConfigItem,
    dbField: string,
    newValue: boolean,
  ): void {
    if (!rowData?.['lading_uuid'] || !node || !this.isFlagEditable(col)) return;

    node.setDataValue(dbField, newValue);
    this.refreshOperationalInsights();
    this.refreshFlagsCell(node);
  }

  private isFlagEditable(col: DynamicGridConfigItem): boolean {
    return !!col.is_editable && !this.blockedFlagFields.has(col.db_field);
  }

  private markFieldReadOnly(dbField: string): void {
    const configItem = this.dynamicConfig.find((item) => item.db_field === dbField);
    if (configItem) {
      configItem.is_editable = false;
    }
  }

  private submitCellUpdate(
    row_uuid: string,
    col: DynamicGridConfigItem,
    dbField: string,
    new_value: any,
    revert: () => void,
  ): void {
    this.cwoService.updateDynamicWorkOrderCell({
      action: 'update_cell',
      entity_target: 'work_orders',
      identity_scope: this.identityScope,
      contact_uuid: this.selectedCarrierContactUuid || undefined,
      row_uuid,
      db_field: col.db_field,
      new_value,
    }).subscribe({
      next: (resp) => {
        if (!resp || resp.success !== true) {
          if ((resp?.message || '').toLowerCase().includes('strictly read-only')) {
            this.markFieldReadOnly(dbField);
          }

          revert();
          console.warn(`Cell update rejected: ${resp?.message}`, { field: col.db_field, row_uuid });
        }
      },
      error: (err) => {
        revert();
        console.error('Cell update failed:', err);
      },
    });
  }

  private refreshFlagsCell(node: any): void {
    if (!this.gridApi) return;

    const refreshConfig: any = {
      force: true,
      columns: ['__flags'],
    };

    if (node) {
      refreshConfig.rowNodes = [node];
    }

    this.gridApi.refreshCells(refreshConfig);
  }

  private cellKey(row_uuid: string, dbField: string): string {
    return `${row_uuid}::${dbField}`;
  }

  private muteOnce(row_uuid: string, dbField: string, fn: () => void): void {
    const key = this.cellKey(row_uuid, dbField);
    this.mutedEdits.add(key);
    try {
      fn();
    } finally {
      setTimeout(() => this.mutedEdits.delete(key), 0);
    }
  }

  private normalizeNewValue(col: DynamicGridConfigItem, value: any): any {
    if (col.input_type === 'date' && (value === '' || value === undefined)) return null;
    return value;
  }

  private dateValueParser = (p: ValueParserParams): any => {
    const v: any = (p as any).newValue;
    if (v === '' || v === undefined) return null;
    return this.normalizeDateEditorValue(v);
  };

  private normalizeDateEditorValue(value: any): any {
    if (!(value instanceof Date)) return value;

    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private formatDateValue(value: any): string {
    if (!value) return '';

    const text = String(value).trim();
    const dateOnly = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateOnly) {
      const localDate = new Date(
        Number(dateOnly[1]),
        Number(dateOnly[2]) - 1,
        Number(dateOnly[3]),
      );
      return localDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    }

    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return text;

    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  private formatDateTimeValue(value: any): string {
    if (!value) return '';

    const text = String(value).trim();
    const normalizedText = this.normalizeDateTimeText(text);
    const date = value instanceof Date ? value : new Date(normalizedText);

    if (Number.isNaN(date.getTime())) return text;

    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  private normalizeDateTimeText(value: string): string {
    let normalized = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(value)
      ? value.replace(' ', 'T')
      : value;

    if (/[+-]\d{2}$/.test(normalized)) {
      normalized += ':00';
    }

    return normalized;
  }

  private isStatusField(dbField: string): boolean {
    return dbField === 'v_ladings.lading_status';
  }

  private isDateTimeField(col: DynamicGridConfigItem, dbField: string): boolean {
    return col.input_type === 'datetime'
      || dbField === 'v_ladings.appointment_start'
      || dbField === 'v_ladings.appointment_end';
  }

  private isDateField(col: DynamicGridConfigItem, dbField: string): boolean {
    return col.input_type === 'date'
      || dbField === 'v_ladings.load_start'
      || dbField === 'v_ladings.estimated_load_start';
  }

  private configureStatusCol(def: ColDef): void {
    def.editable = false;
    def.cellClass = 'cell-readonly cell-status';
    def.minWidth = 174;
    def.flex = 0.9;
    def.cellRenderer = (params: ICellRendererParams) => this.buildStatusCell(params.value);
    def.valueFormatter = (params: ValueFormatterParams) => String(params.value ?? '').trim();
  }

  private buildStatusCell(value: any): HTMLElement | string {
    const status = String(value ?? '').trim();
    if (!status) return '';

    const normalized = status.toLowerCase();
    let pillClasses = 'border-[color:var(--cwo-status-neutral-border)] bg-[var(--cwo-status-neutral-bg)] text-[color:var(--cwo-status-neutral-text)]';
    let dotClasses = 'bg-[var(--cwo-status-neutral-dot)]';

    if (normalized.includes('delivered')) {
      pillClasses = 'border-[color:var(--cwo-status-success-border)] bg-[var(--cwo-status-success-bg)] text-[color:var(--cwo-status-success-text)]';
      dotClasses = 'bg-[var(--cwo-status-success-dot)]';
    } else if (normalized.includes('cancel')) {
      pillClasses = 'border-[color:var(--cwo-status-danger-border)] bg-[var(--cwo-status-danger-bg)] text-[color:var(--cwo-status-danger-text)]';
      dotClasses = 'bg-[var(--cwo-status-danger-dot)]';
    } else if (
      normalized.includes('qualify')
      || normalized.includes('pending')
      || normalized.includes('appointment')
    ) {
      pillClasses = 'border-[color:var(--cwo-status-warning-border)] bg-[var(--cwo-status-warning-bg)] text-[color:var(--cwo-status-warning-text)]';
      dotClasses = 'bg-[var(--cwo-status-warning-dot)]';
    } else if (
      normalized.includes('route')
      || normalized.includes('transit')
      || normalized.includes('loaded')
      || normalized.includes('pickup')
    ) {
      pillClasses = 'border-[color:var(--cwo-status-active-border)] bg-[var(--cwo-status-active-bg)] text-[color:var(--cwo-status-active-text)]';
      dotClasses = 'bg-[var(--cwo-status-active-dot)]';
    }

    const pill = document.createElement('span');
    pill.className = `inline-flex max-w-full items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-extrabold leading-none shadow-sm ${pillClasses}`;
    pill.title = status;

    const dot = document.createElement('span');
    dot.className = `h-1.5 w-1.5 flex-none rounded-full ${dotClasses}`;
    dot.setAttribute('aria-hidden', 'true');

    const label = document.createElement('span');
    label.className = 'truncate whitespace-nowrap';
    label.textContent = status;

    pill.append(dot, label);
    return pill;
  }

  private buildDateCell(value: any, includeTime: boolean): HTMLElement | string {
    if (!value) return '';

    const text = String(value).trim();
    const normalizedText = this.normalizeDateTimeText(text);
    const dateOnly = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
    const date = includeTime
      ? (value instanceof Date ? value : new Date(normalizedText))
      : (dateOnly
        ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
        : (value instanceof Date ? value : new Date(value)));

    if (Number.isNaN(date.getTime())) {
      return text;
    }

    const dateLabel = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    const timeLabel = includeTime
      ? date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        })
      : '';

    const pill = document.createElement('span');
    pill.className = 'inline-flex max-w-full items-center gap-2 rounded-lg border border-[color:var(--cwo-date-border)] bg-[var(--cwo-date-bg)] px-2.5 py-1 text-[11px] font-bold leading-tight text-[color:var(--cwo-date-text)] shadow-sm';
    pill.title = includeTime ? `${dateLabel} at ${timeLabel}` : dateLabel;

    const icon = document.createElement('i');
    icon.className = includeTime
      ? 'fa-solid fa-clock flex-none text-[10px] text-[color:var(--cwo-date-icon)]'
      : 'fa-solid fa-calendar-day flex-none text-[10px] text-[color:var(--cwo-date-icon)]';
    icon.setAttribute('aria-hidden', 'true');

    if (includeTime) {
      const copy = document.createElement('span');
      copy.className = 'flex min-w-0 flex-col leading-[1.05]';

      const dateLine = document.createElement('span');
      dateLine.className = 'truncate whitespace-nowrap text-[11px] font-extrabold';
      dateLine.textContent = dateLabel;

      const timeLine = document.createElement('span');
      timeLine.className = 'mt-0.5 whitespace-nowrap text-[10px] font-semibold text-[color:var(--cwo-date-time-text)]';
      timeLine.textContent = timeLabel;

      copy.append(dateLine, timeLine);
      pill.append(icon, copy);
    } else {
      const label = document.createElement('span');
      label.className = 'truncate whitespace-nowrap';
      label.textContent = dateLabel;
      pill.append(icon, label);
    }

    return pill;
  }

  private coerceBooleanValue(raw: any): boolean {
    return raw === true || raw === 'true' || raw === 't' || raw === '1' || raw === 1;
  }

  private configureDateCol(def: ColDef, col: DynamicGridConfigItem, dbField: string): void {
    def.cellClass = col.is_editable ? 'cell-editable cell-date' : 'cell-readonly cell-date';
    def.minWidth = 154;
    def.flex = 0.86;

    if (col.is_editable) {
      def.editable = true;
      def.cellEditor = 'agDateStringCellEditor';
    }

    def.valueParser = this.dateValueParser;

    def.valueGetter = (p) => (p.data ? p.data[dbField] : null);
    def.valueSetter = (p) => {
      if (!p.data) return false;
      p.data[dbField] = p.newValue;
      return true;
    };
    def.filterValueGetter = (p) => (p.data ? p.data[dbField] : null);

    def.valueFormatter = (params: ValueFormatterParams) => {
      return this.formatDateValue(params.value);
    };
    def.cellRenderer = (params: ICellRendererParams) => this.buildDateCell(params.value, false);
  }

  private configureDateTimeCol(def: ColDef, col: DynamicGridConfigItem, dbField: string): void {
    def.editable = false;
    def.cellClass = 'cell-readonly cell-date';
    def.minWidth = 176;
    def.flex = 0.94;
    def.valueGetter = (p) => (p.data ? p.data[dbField] : null);
    def.filterValueGetter = (p) => (p.data ? p.data[dbField] : null);
    def.valueFormatter = (params: ValueFormatterParams) => this.formatDateTimeValue(params.value);
    def.cellRenderer = (params: ICellRendererParams) => this.buildDateCell(params.value, true);
  }

  private onDynamicCellValueChanged(e: NewValueParams, col: DynamicGridConfigItem, dbField: string): void {
    if (!e.data) return;

    const row_uuid = (e.data as any)['lading_uuid'];
    if (!row_uuid) return;

    const key = this.cellKey(row_uuid, dbField);
    if (this.mutedEdits.has(key)) return;
    if (e.newValue === e.oldValue) return;

    const new_value = this.normalizeNewValue(col, e.newValue);

    this.refreshOperationalInsights();

    this.submitCellUpdate(row_uuid, col, dbField, new_value, () => {
      this.muteOnce(row_uuid, dbField, () => {
        e.node?.setDataValue(dbField, e.oldValue);
      });
      this.refreshOperationalInsights();
      this.refreshFlagsCell(e.node);
    });
  }

  private refreshOperationalInsights(): void {
    const { insights, hasCoverage } = this.buildOperationalInsights();
    this.operationalInsights = insights;
    this.hasOperationalCoverage = hasCoverage;
  }

  private buildOperationalInsights(): { insights: CarrierWorkOrdersInsight[]; hasCoverage: boolean } {
    const insights: CarrierWorkOrdersInsight[] = [];
    let hasCoverage = false;

    if (this.hasVisibleFields('v_lading_site_checks.technician_required', '__technician_assignment')) {
      hasCoverage = true;
      const missingAssignments = this.rowData.filter((row) =>
        this.coerceBooleanValue(row?.['v_lading_site_checks.technician_required']) &&
        this.isBlankValue(row?.['__technician_assignment'])
      ).length;

      insights.push({
        key: 'tech-assignment',
        title: missingAssignments > 0 ? 'Need tech assignment' : 'Tech assignments covered',
        value: missingAssignments,
        description: missingAssignments > 0
          ? 'Technician-required orders without an assigned tech.'
          : 'Every technician-required order in this view already has an assignment.',
        tone: missingAssignments > 0 ? 'rose' : 'emerald',
        icon: 'fa-screwdriver-wrench',
      });
    }

    if (this.hasVisibleFields('v_lading_site_checks.site_survey_required', 'v_lading_site_checks.site_survey_completed')) {
      hasCoverage = true;
      const pendingSurveys = this.rowData.filter((row) =>
        this.coerceBooleanValue(row?.['v_lading_site_checks.site_survey_required']) &&
        !this.coerceBooleanValue(row?.['v_lading_site_checks.site_survey_completed'])
      ).length;

      insights.push({
        key: 'site-survey',
        title: pendingSurveys > 0 ? 'Site surveys pending' : 'Site surveys current',
        value: pendingSurveys,
        description: pendingSurveys > 0
          ? 'Required site surveys that still need to be filed.'
          : 'Every required site survey in this view is already complete.',
        tone: pendingSurveys > 0 ? 'amber' : 'emerald',
        icon: 'fa-clipboard-list',
      });
    }

    if (this.hasVisibleFields('v_ladings.appointment_start') || this.hasVisibleFields('__appointment_window')) {
      hasCoverage = true;
      const missingAppointments = this.rowData.filter((row) =>
        this.isBlankValue(this.appointmentWindowStartValue(row))
      ).length;

      insights.push({
        key: 'delivery-appointment',
        title: missingAppointments > 0 ? 'Missing delivery appointments' : 'Delivery appointments set',
        value: missingAppointments,
        description: missingAppointments > 0
          ? 'Orders still missing a scheduled delivery appointment.'
          : 'Every order in this view has a delivery appointment scheduled.',
        tone: missingAppointments > 0 ? 'amber' : 'emerald',
        icon: 'fa-calendar-days',
      });
    }

    if (this.hasVisibleFields('v_lading_site_checks.customer_confirmed')) {
      hasCoverage = true;
      const unconfirmedOrders = this.rowData.filter((row) =>
        !this.coerceBooleanValue(row?.['v_lading_site_checks.customer_confirmed'])
      ).length;

      insights.push({
        key: 'customer-confirmation',
        title: unconfirmedOrders > 0 ? 'Customer confirmations open' : 'Customer confirmations current',
        value: unconfirmedOrders,
        description: unconfirmedOrders > 0
          ? 'Orders that still need customer confirmation.'
          : 'Customer confirmation is recorded across this view.',
        tone: unconfirmedOrders > 0 ? 'sky' : 'emerald',
        icon: 'fa-phone-volume',
      });
    }

    if (this.hasVisibleFields('v_ladings.estimated_load_start')) {
      hasCoverage = true;
      const missingTargetDates = this.rowData.filter((row) =>
        this.isBlankValue(row?.['v_ladings.estimated_load_start'])
      ).length;

      insights.push({
        key: 'target-date',
        title: missingTargetDates > 0 ? 'Target dates missing' : 'Target dates covered',
        value: missingTargetDates,
        description: missingTargetDates > 0
          ? 'Orders that still need a target pickup date.'
          : 'Every order in this view has a target pickup date.',
        tone: missingTargetDates > 0 ? 'sky' : 'emerald',
        icon: 'fa-bullseye',
      });
    }

    return {
      insights: insights.filter((insight) => insight.value > 0).slice(0, 3),
      hasCoverage,
    };
  }

  private appointmentWindowStartValue(row: any): any {
    const directStart = row?.['v_ladings.appointment_start'];
    if (!this.isBlankValue(directStart)) return directStart;

    const rawWindow = row?.['__appointment_window'];
    if (!rawWindow) return directStart;

    if (typeof rawWindow === 'object') {
      return (rawWindow as any).start ?? directStart;
    }

    if (typeof rawWindow !== 'string') return directStart;

    try {
      const parsed = JSON.parse(rawWindow);
      return parsed?.start ?? directStart;
    } catch {
      return directStart;
    }
  }

  private hasVisibleFields(...fields: string[]): boolean {
    return fields.every((field) =>
      this.dynamicConfig.some((item) => item.is_visible && item.db_field === field)
    );
  }

  private isBlankValue(value: any): boolean {
    return value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
  }

  private bindGridStateListeners(): void {
    if (!this.gridApi || this.gridStateListenersBound) return;

    const saveState = () => this.persistGridState();
    const events = [
      'columnMoved',
      'columnPinned',
      'columnResized',
      'columnVisible',
      'sortChanged',
      'filterChanged',
      'paginationChanged',
    ] as const;

    events.forEach((eventName) => this.gridApi.addEventListener(eventName, saveState));
    this.gridStateListenersBound = true;
  }

  private persistGridState(): void {
    if (!this.gridApi || this.isRestoringGridState) return;

    const api: any = this.gridApi;
    const gridState: CarrierWorkOrdersGridState = {
      columnState: this.gridApi.getColumnState(),
      filterModel: this.gridApi.getFilterModel(),
      paginationPageSize: this.normalizePaginationPageSize(api.paginationGetPageSize?.()),
    };

    window.localStorage.setItem(this.gridStateStorageKey, JSON.stringify(gridState));
  }

  private restoreGridState(): void {
    if (!this.gridApi) return;

    const savedState = this.readGridState();
    if (!savedState) return;

    this.isRestoringGridState = true;

    try {
      if (savedState.columnState?.length) {
        this.gridApi.applyColumnState({
          state: savedState.columnState,
          applyOrder: true,
        });
      }

      if (savedState.filterModel) {
        this.gridApi.setFilterModel(savedState.filterModel);
      }

      const pageSize = this.normalizePaginationPageSize(savedState.paginationPageSize);
      if (pageSize) {
        this.setPaginationPageSize(pageSize);
      }
    } catch (error) {
      console.warn('Failed to restore carrier work orders grid state', error);
    } finally {
      window.setTimeout(() => {
        this.isRestoringGridState = false;
      }, 0);
    }
  }

  private readGridState(): CarrierWorkOrdersGridState | null {
    const raw = window.localStorage.getItem(this.gridStateStorageKey);
    if (!raw) return null;

    try {
      const parsed = JSON.parse(raw) as CarrierWorkOrdersGridState;
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      window.localStorage.removeItem(this.gridStateStorageKey);
      return null;
    }
  }

  private normalizePaginationPageSize(pageSize: unknown): number {
    const numericValue = Number(pageSize);
    const allowedPageSizes = this.gridOptions.paginationPageSizeSelector;

    if (Array.isArray(allowedPageSizes) && allowedPageSizes.includes(numericValue)) {
      return numericValue;
    }

    return this.gridOptions.paginationPageSize ?? 50;
  }

  private setPaginationPageSize(pageSize: number): void {
    const api: any = this.gridApi;
    if (!api) return;

    if (typeof api.setGridOption === 'function') {
      api.setGridOption('paginationPageSize', pageSize);
      return;
    }

    if (typeof api.paginationSetPageSize === 'function') {
      api.paginationSetPageSize(pageSize);
      return;
    }

    this.gridOptions = {
      ...this.gridOptions,
      paginationPageSize: pageSize,
    };
  }
}
